import { locationRoute } from "@/App";
import { MenuItem } from "@/hooks/useMenu";
import { cn, formatPriceToDkk, getImageUrl } from "@/lib/utils";
import { MenuContext } from "@/routes/Menu";
import { CartItem, useCartStore } from "@/store/CartStore";
import { useContext, useState } from "react";
import {
  motion,
  useAnimate,
  // useAnimationControls,
  // useDragControls,
} from "motion/react";
import Loader from "./Loader";
import { CirclePlusIcon, TrashIcon } from "lucide-react";

function getUnavailableBadgeText(pausedUntil?: string | null): string {
  if (!pausedUntil) {
    return "Unavailable";
  }

  const pausedDate = new Date(pausedUntil);
  const now = new Date();
  const diffInMinutes = (pausedDate.getTime() - now.getTime()) / (1000 * 60);

  if (diffInMinutes < 30) {
    return "Temporarily unavailable";
  }

  return "Out of stock";
}

function ProductItem({
  item,
  cartItem,
}: {
  item: MenuItem;
  cartItem?: CartItem;
}) {
  const ENABLE_DRAG_ACTIONS = false;

  const {
    setUnavailableProductOpen,
    setCounterOnlyProductOpen,
    setUnavailableProductPausedUntil,
    handleOpenProduct,
    handleEditCartItem,
  } = useContext(MenuContext);

  const { locationId } = locationRoute.useParams();
  const { add, cart, setCount } = useCartStore();

  if (item === undefined) return;

  const requiredAddons =
    item.addonGroups !== undefined
      ? item.addonGroups
          ?.map((group) => group.items?.map((addon) => addon))
          .flat()
          .filter((addon) => addon.min > 0)
      : [];
  const requiredAddonCost =
    requiredAddons.reduce(
      (total, addon) => total + addon.price * addon.min,
      0,
    ) ?? 0;

  const defaultOrCheapestOption =
    item.options.find((option) => option.default) ?? item.options[0];

  const priceToUse = cartItem
    ? cartItem.price * cartItem.count +
      (cartItem?.addons !== undefined
        ? cartItem?.addons?.reduce(
            (total, addon) =>
              total + addon.price * addon.count * cartItem.count,
            0,
          )
        : 0)
    : defaultOrCheapestOption?.price + requiredAddonCost;

  const quantityInCart = cartItem
    ? cartItem.count
    : cart.reduce(
        (acc, itemInCart) =>
          itemInCart.productId === item.id ? acc + itemInCart.count : acc,
        0,
      );

  const isInCart = quantityInCart > 0;

  const hasImage = item.image && item.image !== "/images/irondice.jpeg";
  const [isLoading, setIsLoading] = useState(false);

  function handleAddToCart() {
    setIsLoading(true);
    const variant = defaultOrCheapestOption;
    // @ts-expect-error - @nils, vi skal lige kigge på den her tror jeg.
    add({ ...variant, addons: [] }, 1);
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  }

  function handleRemoveFromCart() {
    if (!cartItem) return;
    setIsLoading(true);
    setCount(cartItem?.lineId, 0);
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  }

  const [scope, animate] = useAnimate();
  function handleDragEnd(
    _: unknown,
    info: { offset: { x: number }; velocity: { x: number } },
  ) {
    const offset = info.offset.x;
    //const velocity = info.velocity.x;

    const THRESHOLD = 150;

    if (offset > THRESHOLD) {
      animate(scope.current, { x: 0, opacity: 1 }, { duration: 0.5 });
      setTimeout(() => handleAddToCart(), 200);
    } else if (offset < THRESHOLD * -1) {
      animate(scope.current, { x: 0, opacity: 1 }, { duration: 0.5 });
      setTimeout(() => handleRemoveFromCart(), 200);
    } else {
      animate(scope.current, { x: 0, opacity: 1 }, { duration: 0.5 });
    }
  }

  return (
    <motion.div
      key={item.id}
      className={cn("relative transition text-left h-full flex flex-col")}
      whileTap={{ cursor: "grabbing" }}
      //layout
      transition={{ ease: "linear" }}
    >
      <div
        className={cn(
          "absolute inset-0 w-full h-full",
          isInCart ? "grid-cols-2" : "grid-cols-1",
          ENABLE_DRAG_ACTIONS ? "grid" : "hidden",
        )}
      >
        <div className="px-4 bg-primary h-full w-full z-[0] flex items-center justify-start">
          {isLoading ? (
            <Loader />
          ) : (
            <CirclePlusIcon className="text-primary-foreground" size={20} />
          )}
        </div>
        {isInCart && (
          <div className="px-4 bg-red-500 h-full w-full z-[0] flex items-center justify-end">
            {isLoading ? (
              <Loader />
            ) : (
              <TrashIcon className="text-primary-foreground" size={20} />
            )}
          </div>
        )}
      </div>
      <motion.button
        ref={scope}
        drag={ENABLE_DRAG_ACTIONS ? "x" : undefined}
        dragDirectionLock={ENABLE_DRAG_ACTIONS ? true : undefined}
        onDragEnd={ENABLE_DRAG_ACTIONS ? handleDragEnd : undefined}
        className={cn(
          "relative overflow-hidden py-2 px-3 text-left first:pt-2 bg-background w-full",
        )}
        transition={{ ease: "linear" }}
        onClick={(_) => {
          if (item?.unavailable === true) {
            setUnavailableProductPausedUntil(item.paused_until ?? null);
            setUnavailableProductOpen(true);
            return;
          }

          if (item?.can_purchase === false) {
            setCounterOnlyProductOpen(true);
            return;
          }

          cartItem !== undefined && handleEditCartItem
            ? handleEditCartItem(cartItem)
            : handleOpenProduct(item.id);
        }}
        type="button"
      >
        <div
          className={cn("grid grid-cols-6 py-2 gap-x-4 justify-between", {
            "opacity-40": item?.unavailable === true,
          })}
        >
          <div
            className={cn(
              "flex flex-col justify-between  gap-y-2 shrink w-full",
              hasImage ? "col-span-4" : "col-span-full",
            )}
          >
            <div className="">
              <h3 className="text-md font-sans tracking-tight text-foreground flex items-center gap-x-2 -mt-1 pr-4 relative w-max max-w-full">
                <span>{item.name}</span>

                {isInCart && (
                  <div className="absolute top-0 -right-1 font-normal size-3 text-[10px] p-2 rounded-full bg-primary text-primary-foreground flex items-center justify-center z-[2]">
                    <span>{quantityInCart}</span>
                  </div>
                )}
              </h3>

              {item.description && (
                <p className="text-[10px] text-muted-foreground whitespace-pre-line line-clamp-2">
                  {item.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-x-2">
              <p className="text-sm font-medium tracking-tight">
                {formatPriceToDkk(priceToUse ?? 0.0)}
              </p>
              {item.unavailable && (
                <span className="text-[10px] py-1 px-2 bg-gray-100 text-gray-700 rounded-full font-medium">
                  {getUnavailableBadgeText(item.paused_until)}
                </span>
              )}
              {!item.unavailable && item.can_purchase === false && (
                <span className="text-[10px] py-1 px-2 bg-orange-100 text-orange-700 rounded-full font-medium">
                  Order at the kitchen counter
                </span>
              )}
            </div>
          </div>

          {hasImage && (
            <div className="relative w-full col-span-2 flex  items-center">
              {/*isInCart && (
              <div className="absolute -top-2 -right-2 font-normal size-4 text-[11px] p-3 rounded-full bg-primary text-primary-foreground flex items-center justify-center z-[2]">
                <span>{quantityInCart}</span>
              </div>
            )*/}
              <div className="relative rounded-lg aspect-video overflow-hidden w-full h-full ml-auto md:max-w-[60%]">
                <img
                  src={getImageUrl(locationId, item.image)}
                  alt={""}
                  loading="lazy"
                  width="48"
                  height="48"
                  className="absolute inset-0 w-full h-full object-cover bg-gray-300 scale-110"
                />
              </div>
            </div>
          )}
        </div>
        {cartItem && (
          <span className="text-[10px] py-1 px-2 select-none pointer-events-none bg-muted text-muted-foreground w-max max-w-[60%] overflow-hidden whitespace-nowrap text-ellipsis rounded-full">
            {cartItem.count}x: {cartItem.name}
            {cartItem.addons !== undefined &&
              cartItem.addons?.length > 0 &&
              ` + ${cartItem.addons
                ?.map((addon) => `${addon.count}x ${addon.name}`)
                ?.join(", ")}`}
          </span>
        )}
      </motion.button>
    </motion.div>
  );
}

export { ProductItem };
